import { type Editor } from '@tiptap/core';
import { Selection } from '@tiptap/pm/state';

/**
 * Pure section keyboard-navigation handlers, shared by the vMarkdown and
 * markdown-native section nodes. They operate only on the shared section schema
 * (`sectionNode` containing a leading `heading` then body blocks), so both
 * editor systems delegate to them rather than each carrying a copy — the same
 * shared-leaf pattern as `apply-section-input-rule`.
 *
 * Each returns `true` when it handled the key (so the keymap stops), `false`
 * to fall through to default behaviour.
 */

/**
 * Enter on the section's leading heading (the title) drops the cursor into the
 * start of the section body (the block after the heading) instead of splitting
 * the heading. Content headings (H2–H5) in the body keep default Enter.
 */
export function handleSectionHeadingEnter(editor: Editor): boolean {
  const { $from } = editor.state.selection;

  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name !== 'heading') continue;

    const parent = $from.node(depth - 1);
    if (parent.type.name !== 'sectionNode') break;

    const headingIndex = $from.index(depth - 1);
    if (headingIndex !== 0) break;
    if (headingIndex + 1 >= parent.childCount) break;

    // The first body block may not be a textblock (e.g. a list), where the raw
    // `after + 1` position is invalid and would strand the cursor.
    const target = Selection.near(editor.state.doc.resolve($from.after(depth)), 1).from;
    editor.commands.setTextSelection(target);
    return true;
  }

  return false;
}

const findSectionContext = (editor: Editor) => {
  const { $from } = editor.state.selection;

  let sectionDepth: number | null = null;
  for (let depth = $from.depth; depth > 0; depth--) {
    if ($from.node(depth).type.name === 'sectionNode') {
      sectionDepth = depth;
      break;
    }
  }
  if (sectionDepth === null) return null;

  // Only intercept when the cursor sits in a top-level child of the section
  // (heading or body block). Anything deeper (lists, tables) keeps its own
  // Tab handler — sinkListItem / liftListItem etc.
  if ($from.depth !== sectionDepth + 1) return null;

  const section = $from.node(sectionDepth);
  const blockIndex = $from.index(sectionDepth);
  return {
    $from,
    section,
    sectionDepth,
    sectionStart: $from.before(sectionDepth),
    blockIndex,
    isHeading: section.child(blockIndex).type.name === 'heading',
  };
};

/**
 * Tab moves forward through sections: heading → body, then body → next
 * section's heading.
 */
export function handleSectionTab(editor: Editor): boolean {
  const ctx = findSectionContext(editor);
  if (!ctx) return false;

  const { $from, section, sectionDepth, sectionStart, blockIndex, isHeading } = ctx;

  if (isHeading && blockIndex + 1 < section.childCount) {
    editor.commands.setTextSelection($from.after(sectionDepth + 1) + 1);
    return true;
  }

  const nextSectionStart = sectionStart + section.nodeSize;
  const nextSection = editor.state.doc.nodeAt(nextSectionStart);
  if (nextSection?.type.name === 'sectionNode') {
    editor.commands.setTextSelection(nextSectionStart + 2);
    return true;
  }

  return false;
}

/**
 * Shift-Tab moves backward: body → this section's heading, then heading →
 * previous section's heading.
 */
export function handleSectionShiftTab(editor: Editor): boolean {
  const ctx = findSectionContext(editor);
  if (!ctx) return false;

  const { sectionStart, isHeading } = ctx;

  if (!isHeading) {
    editor.commands.setTextSelection(sectionStart + 2);
    return true;
  }

  if (sectionStart === 0) return false;
  const $prev = editor.state.doc.resolve(sectionStart);
  if ($prev.nodeBefore?.type.name !== 'sectionNode') return false;
  editor.commands.setTextSelection(sectionStart - 2);
  return true;
}
